import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import FilterSidebar from "./FilterSidebar";
import ProductGrid from "./ProductGrid";
import { Footer } from "../common";
import { listProducts } from "../../services/explore";
import http from "../../lib/http";
import "../../styles/ExploreScreen.css";

const normalizeHttps = (u) =>
  typeof u === "string" ? u.replace("http://", "https://") : u;

/* ── Icons ── */
const FilterIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const GridIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>;
const ListIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
const ChevronDown = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;
const CheckIcon   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

const SORT_OPTIONS = [
  { key: "default",    label: "Relevance",          api: null },
  { key: "price_asc",  label: "Price: Low to High", api: "price_asc" },
  { key: "price_desc", label: "Price: High to Low", api: "price_desc" },
  { key: "most_sold",  label: "Best Sellers",        api: "most_sold" },
  { key: "new",        label: "New Arrivals",        api: "new" },
  { key: "featured",   label: "Our Picks",           api: "featured" },
];

/* ── Price resolver ── */
const resolvePrice = (p) => {
  const final    = Number(p.final_price            ?? 0);
  const flash    = Number(p.flash_sale_price       ?? 0);
  const discount = Number(p.product_discount_price ?? 0);
  const regular  = Number(p.price                  ?? 0);
  if (final    > 0) return final;
  if (flash    > 0) return flash;
  if (discount > 0) return discount;
  return regular;
};

/* ── Map raw API product → UI shape ── */
const mapProduct = (p) => ({
  id           : p.id,
  slug         : p.slug,
  name         : p.name,
  price        : resolvePrice(p),
  originalPrice: Number(p.price ?? 0),
  isFlashSale  : !!p.is_flash_sale,
  pricingType  : p.pricing_type || "normal",
  // ✅ product_images is now a string[] — use index 0, fall back to image_url
  image: normalizeHttps(
    Array.isArray(p.product_images) && p.product_images.length
      ? p.product_images[0]
      : p.image_url ?? ""
  ),
  rating      : Number(p.average_rating ?? 0),
  reviews     : Number(p.total_reviews  ?? 0),
  sold        : `${Number(p.sold_count  ?? 0)} sold`,
  // ✅ category is now a nested object
  categoryName: p.category?.category_name ?? "",
  categoryId  : p.category?.id            ?? null,
});

const ExploreScreen = () => {
  const location        = useLocation();
  const sortDropdownRef = useRef(null);

  // ── Read incoming state from navigation ──────────────────────────────────
  const incomingCategory = location.state?.selectedCategory || null;

  // ✅ Read ?q= query string set by HeroSection's search button
  const urlSearchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("q")?.trim() || "";
  }, [location.search]);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [sortOpen,    setSortOpen]    = useState(false);
  const [activeSort,  setActiveSort]  = useState("default");
  const [loading,     setLoading]     = useState(false);
  const [products,    setProducts]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [page,        setPage]        = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount,  setTotalCount]  = useState(0);

  // ✅ Search query state — pre-filled from URL ?q= param
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);

  const LIMIT = 24;
  const [viewMode, setViewMode] = useState("grid");

  /* ── Initialise filters ── */
  const [filters, setFilters] = useState(() => ({
    category: {
      all             : !incomingCategory,
      selectedId      : incomingCategory?.id   ?? null,
      selectedName    : incomingCategory?.category_name || incomingCategory?.name || null,
      incomingCategory: incomingCategory || null,
    },
    priceRange: { min: 0, max: 5000, fromValue: 0, toValue: 5000 },
  }));

  /* ── Close sort dropdown on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target))
        setSortOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── When URL ?q= changes (e.g. user navigates from hero search), sync state ── */
  useEffect(() => {
    if (urlSearchQuery !== searchQuery) {
      setSearchQuery(urlSearchQuery);
      setPage(1);
      setTotalCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchQuery]);

  const updateFilters = (newFilters) => {
    setPage(1);
    setTotalCount(0);
    setFilters(newFilters);
  };

  /* ── Load categories ── */
  useEffect(() => {
    (async () => {
      try {
        const res  = await http.get("/api/customer/categories");
        const list = res?.data?.data ?? [];
        setCategories(Array.isArray(list) ? list : []);
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  /* ── Sync incoming category from navigation state ── */
  useEffect(() => {
    if (!incomingCategory?.id) return;
    setFilters((prev) => {
      if (String(prev.category.selectedId) === String(incomingCategory.id)) return prev;
      return {
        ...prev,
        category: {
          all             : false,
          selectedId      : incomingCategory.id,
          selectedName    : incomingCategory.category_name || incomingCategory.name || null,
          incomingCategory: incomingCategory,
        },
      };
    });
    setPage(1);
    setTotalCount(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingCategory?.id]);

  /* ── Build query params ── */
  const buildQueryFromFilters = useCallback(() => {
    const params     = { page, limit: LIMIT };
    const selectedId = filters?.category?.selectedId;

    // Category filter — API uses category_name
    if (selectedId) {
      const catName =
        filters.category.selectedName ||
        categories.find((c) => Number(c?.id) === Number(selectedId))?.category_name ||
        categories.find((c) => Number(c?.id) === Number(selectedId))?.name ||
        filters.category.incomingCategory?.category_name ||
        filters.category.incomingCategory?.name ||
        null;

      if (catName) {
        params.category_name = catName.trim();
      } else {
        console.warn("[EXPLORE] No category name found for id:", selectedId);
      }
    }

    // Price range
    const from = Number(filters?.priceRange?.fromValue || 0);
    const to   = Number(filters?.priceRange?.toValue   || filters?.priceRange?.max || 5000);
    const max  = Number(filters?.priceRange?.max        || 5000);
    if (from > 0)           params.min_price = from;
    if (to > 0 && to < max) params.max_price = to;

    // Sort
    const sortOpt = SORT_OPTIONS.find((o) => o.key === activeSort);
    if (sortOpt?.api) params.sort = sortOpt.api;

    // ✅ Search query (from URL ?q= or local state)
    if (searchQuery.trim()) params.query = searchQuery.trim();

    return params;
  }, [filters, page, categories, activeSort, searchQuery]);

  /* ── Fetch products ── */
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params   = buildQueryFromFilters();
      const { data } = await listProducts(params);

      // ✅ New API shape: { data: [...], total_products, returned_count, pagination }
      const arr = Array.isArray(data?.data) ? data.data
                : Array.isArray(data)        ? data
                : [];

      let mapped = arr.map(mapProduct);

      // Client-side sort fallback (in case API doesn't handle it)
      if (activeSort === "price_asc")  mapped = [...mapped].sort((a, b) => a.price - b.price);
      if (activeSort === "price_desc") mapped = [...mapped].sort((a, b) => b.price - a.price);

      setProducts(mapped);

      // ── Pagination from API ──────────────────────────────────────────────
      // Prefer API-provided total_products; fall back to inference
      const apiTotal    = data?.total_products ?? data?.total ?? null;
      const apiReturned = data?.returned_count ?? mapped.length;
      const lastPage    = data?.pagination?.last_page ?? null;

      if (apiTotal !== null) {
        setTotalCount(Number(apiTotal));
        // Use last_page from pagination object if available, otherwise calculate
        if (lastPage !== null) {
          setHasNextPage(page < Number(lastPage));
        } else {
          setHasNextPage(page * LIMIT < Number(apiTotal));
        }
      } else {
        setTotalCount(page === 1 ? apiReturned : (page - 1) * LIMIT + apiReturned);
        setHasNextPage(apiReturned === LIMIT);
      }
      // ────────────────────────────────────────────────────────────────────

    } catch (e) {
      console.error("[EXPLORE] fetch failed:", e);
      setProducts([]);
      setTotalCount(0);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [buildQueryFromFilters, activeSort, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  /* ── Derived ── */
  const activeCategoryName = useMemo(() => {
    if (filters.category.all || !filters.category.selectedId) return null;
    return (
      filters.category.selectedName ||
      categories.find((c) => Number(c?.id) === Number(filters.category.selectedId))?.category_name ||
      filters.category.incomingCategory?.name ||
      null
    );
  }, [filters.category, categories]);

  const activeSortLabel = SORT_OPTIONS.find((o) => o.key === activeSort)?.label || "Relevance";

  const handleSortSelect = (key) => {
    setActiveSort(key);
    setPage(1);
    setTotalCount(0);
    setSortOpen(false);
  };

  const handleResetAll = () => {
    updateFilters({
      category  : { all: true, selectedId: null, selectedName: null, incomingCategory: null },
      priceRange: { min: 0, max: 5000, fromValue: 0, toValue: 5000 },
    });
    setActiveSort("default");
    setSearchQuery("");
  };

  return (
    <div className="es-root">
      <div className="es-container">

        {/* ── Desktop sidebar ── */}
        <aside className="es-sidebar">
          <FilterSidebar
            filters={filters}
            categories={categories}
            onUpdateFilters={updateFilters}
          />
        </aside>

        {/* ── Main ── */}
        <div className="es-main">

          {/* Toolbar */}
          <div className="es-toolbar">
            <div className="es-toolbar-left">
              <button className="es-filter-btn" onClick={() => setIsFilterDrawerOpen(true)}>
                <FilterIcon /> Filters
              </button>

              <div className="es-breadcrumb">
                <span className="es-breadcrumb-root">All Products</span>

                {activeCategoryName && (
                  <>
                    <span className="es-breadcrumb-sep">›</span>
                    <span className="es-breadcrumb-current">{activeCategoryName}</span>
                  </>
                )}

                {/* ✅ Show active search query in breadcrumb */}
                {searchQuery && (
                  <>
                    <span className="es-breadcrumb-sep">›</span>
                    <span className="es-breadcrumb-current">"{searchQuery}"</span>
                  </>
                )}

                {!loading && (
                  <span className="es-result-count">
                    {totalCount} {totalCount === 1 ? "result" : "results"}
                  </span>
                )}
                {loading && (
                  <span className="es-result-count es-result-count--loading">Loading…</span>
                )}
              </div>
            </div>

            <div className="es-toolbar-right">
              {/* Sort dropdown */}
              <div className="es-sort-wrap" ref={sortDropdownRef}>
                <button
                  className={`es-sort-btn ${sortOpen ? "open" : ""}`}
                  onClick={() => setSortOpen((s) => !s)}
                >
                  <span className="es-sort-label">Sort:</span>
                  <strong>{activeSortLabel}</strong>
                  <span className={`es-sort-chevron ${sortOpen ? "flipped" : ""}`}>
                    <ChevronDown />
                  </span>
                </button>

                {sortOpen && (
                  <div className="es-sort-dropdown">
                    {SORT_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        className={`es-sort-option ${activeSort === opt.key ? "active" : ""}`}
                        onClick={() => handleSortSelect(opt.key)}
                      >
                        <span>{opt.label}</span>
                        {activeSort === opt.key && <CheckIcon />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* View toggle */}
              <div className="es-view-toggle">
                <button
                  className={`es-view-btn ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  title="Grid view"
                >
                  <GridIcon />
                </button>
                <button
                  className={`es-view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  title="List view"
                >
                  <ListIcon />
                </button>
              </div>
            </div>
          </div>

          {/* Products */}
          {loading ? (
            <div className="es-loading">
              <div className="es-spinner" />
              <span>Loading products…</span>
            </div>
          ) : products.length === 0 ? (
            <div className="es-empty">
              <div className="es-empty-icon">🔍</div>
              <h3 className="es-empty-title">No products found</h3>
              <p className="es-empty-sub">
                Try adjusting your filters or browse a different category.
              </p>
              <button className="es-empty-reset" onClick={handleResetAll}>
                Reset Filters
              </button>
            </div>
          ) : (
            <div className={`es-grid-wrap ${viewMode === "list" ? "es-list-mode" : ""}`}>
              <ProductGrid products={products} viewMode={viewMode} />
            </div>
          )}

          {/* Pagination */}
          {!loading && products.length > 0 && (
            <div className="es-pagination">
              <button
                className="es-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                ← Prev
              </button>
              <span className="es-page-label">Page {page}</span>
              <button
                className="es-page-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNextPage}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      <br />
      <Footer />
    </div>
  );
};

export default ExploreScreen;